import type { ColumnCatalogDef } from '@/types/column-catalog'
import { SHARED_COLUMNS } from '@/column-registry/shared-columns'
import { COUNTRY_ISO_OPTIONS, YES_NO_INTEGER_OPTIONS } from '@/column-registry/enum-options'
import type { ColumnType } from '@/features/sliced-form/schemas/column-compatibility'
import { defaultColumnType } from '@/components/grid/column-builder'

// ─── History Column Definition ───

export type HistoryColumnDef = ColumnCatalogDef

// ─── Data Type Inference ───

type DataType = ColumnCatalogDef['dataType']

/** Infer data type from a column key suffix. */
function inferDataType(key: string): DataType {
  // Order matters: check specific suffixes before generic ones
  if (
    key.endsWith('_chg_pct') ||
    key.endsWith('_gap_pct') ||
    key.endsWith('_ret_open_pct') ||
    key.endsWith('_high_low_pct') ||
    key.endsWith('_dist_pct') ||
    key.endsWith('_open_high_pct') ||
    key.endsWith('_open_low_pct') ||
    key.endsWith('_close_high_pct') ||
    key.endsWith('_close_low_pct') ||
    key.endsWith('_range_close_pct') ||
    key.endsWith('_pm_high_pct') ||
    key.endsWith('_high_pct') ||
    key.endsWith('_low_pct') ||
    key.endsWith('_close_pct') ||
    key.endsWith('_open_range_pct') ||
    key.endsWith('_pullback_open_j_pct') ||
    key.endsWith('_max_pct') ||
    key.endsWith('_min_pct')
  )
    return 'percent'
  // `_dollar_vol` is a strict suffix of `_vol`, so it's already covered by the
  // `_vol` test below — the `dataType` is `'volume'` either way. The
  // `largePrice` columnType refinement happens in `inferColumnType`.
  if (key.endsWith('_vol') || key.startsWith('day0_adtv_')) return 'volume'
  if (key.endsWith('_date')) return 'date'
  if (key.endsWith('_time') || key.endsWith('_start_time')) return 'string'
  if (key.endsWith('_open_ssr')) return 'enum'
  if (
    key.endsWith('_open') ||
    key.endsWith('_high') ||
    key.endsWith('_low') ||
    key.endsWith('_close') ||
    key.endsWith('_vw') ||
    key.endsWith('_range') ||
    key.endsWith('_chg') ||
    key.endsWith('_gap') ||
    key.endsWith('_ret_open') ||
    key.endsWith('_max') ||
    key.endsWith('_min') ||
    key.endsWith('_max_vw') ||
    key.endsWith('_min_vw')
  )
    return 'currency'
  if (/_ema_\d+$/.test(key) || /_atr_\d+$/.test(key)) return 'currency'
  if (/_ema_\d+_dist_pct$/.test(key)) return 'percent'
  if (key.startsWith('day0_consec_') || key.startsWith('day0_rvol_') || key === 'halts_count')
    return 'number'
  return 'number'
}

// ─── Helper: make a column def ───

/**
 * Infer the measurement-domain `columnType` from a key when no explicit tag
 * is provided. Mirrors the semantics in `scanner-column-defs.ts`:
 *  - `*_dollar_vol` keys are big-dollar magnitudes (legacy `largePrice`),
 *    NOT share-volume, so they must not pair with `*_vol` columns.
 *  - `*_time` / `*_start_time` are wall-clock time strings — sub-domain of
 *    `string` so day0_high_time pairs with day0_low_time / day1_high_time
 *    but not with `country` / `ticker` / etc.
 *  - `*_open_ssr` columns share the SSR option space across days — pair with
 *    each other and with the SSR columns in the scanner catalog.
 *  - `*_trades` (e.g. day0_trades, day_m1_trades) are discrete trade counts
 *    (legacy `largeInteger`) and must not pair with share-counts.
 *  - everything else falls back to `defaultColumnType(dataType)` which keeps
 *    `number` columns at `arbitraryNumber` (default-incomparable).
 */
// Financials columns pulled from the old app / scanner catalog are `dataType:
// 'volume'` (big-number formatting) but semantically large dollar magnitudes
// or percentages, not share counts — mirrors the same override already made
// in `scanner-column-defs.ts` for these exact keys.
const LARGE_PRICE_FINANCIAL_KEYS = new Set([
  'cashandcashequivalents',
  'cashcashequivalentsandshortterminvestments',
  'currentassets',
  'currentliabilities',
  'netincome',
])
const PERCENTAGE_FINANCIAL_KEYS = new Set(['grossmargin', 'netmargin'])

/**
 * Wall-clock columns whose key does not end in `_time`. Every other clock column
 * is recognised by its suffix; a new one that breaks the convention goes here, and
 * `history-column-domains.unit.test.ts` fails if the catalog and this set disagree.
 */
export const CLOCK_COLUMNS_WITHOUT_TIME_SUFFIX: ReadonlySet<string> = new Set([
  'first_halt', // TIME columns on mv_historical_halts_by_day
  'last_halt',
])

function inferColumnType(key: string, dataType: DataType): ColumnType {
  if (key.endsWith('_dollar_vol')) return 'largePrice'
  if (key.endsWith('_time') || CLOCK_COLUMNS_WITHOUT_TIME_SUFFIX.has(key)) return 'string_time'
  // (SSR is an enum dataType — `defaultColumnType('enum')` returns the
  // default-incomparable `'enum'` bucket, which is fine: comparing `day0_open_ssr`
  // value-to-value with Yes/No options is what users actually want; column-to-
  // column comparisons across SSR days are not currently a feature.)
  if (key.endsWith('_trades')) return 'tradeCount'
  if (LARGE_PRICE_FINANCIAL_KEYS.has(key)) return 'largePrice'
  if (PERCENTAGE_FINANCIAL_KEYS.has(key)) return 'percentage'
  return defaultColumnType(dataType)
}

function col(
  key: string,
  label: string,
  dataType: DataType,
  category: string,
  group?: string,
  formLabel?: string,
): HistoryColumnDef {
  const shared = SHARED_COLUMNS[key]
  const l = shared?.label ?? label
  const dt = shared?.dataType ?? dataType
  const ct = shared?.columnType ?? inferColumnType(key, dt)
  // Enum option lists come from one of two places:
  //   - shared meta (country, sector, industry, exchange, share_group, ssr)
  //   - the historical `*_open_ssr` family, which is local to this catalog
  //     and reuses the Yes/No flag list (legacy parity).
  const enumOptions =
    shared?.enumOptions ??
    (key.endsWith('_open_ssr') || key.endsWith('_close_ssr')
      ? YES_NO_INTEGER_OPTIONS
      : undefined) ??
    (key === 'country_iso' ? COUNTRY_ISO_OPTIONS : undefined)
  const base: HistoryColumnDef = {
    key,
    label: l,
    dataType: dt,
    category,
    columnType: ct,
    ...(shared?.comparesTo != null ? { comparesTo: shared.comparesTo } : {}),
    ...(shared?.allowNull ? { allowNull: true } : {}),
    ...(enumOptions != null ? { enumOptions } : {}),
    ...(formLabel != null ? { formLabel } : {}),
  }
  return group ? { ...base, group } : base
}

// ─── Label Helpers ───

/** "day0" → "D[1]", "day1" → "D[2]", "day_m1" → "D[-1]", etc. */
function dayLabel(prefix: string): string {
  if (prefix.startsWith('day_m')) return `D[-${prefix.slice(5)}]`
  return `D[${Number(prefix.slice(3)) + 1}]`
}

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function suffixLabel(suffix: string): string {
  const map: Record<string, string> = {
    _open: 'Open',
    _high: 'High',
    _low: 'Low',
    _close: 'Close',
    _vol: 'Vol',
    _vw: 'Vwap',
    _range: 'Range',
    _chg: 'Chg $',
    _chg_pct: 'Chg %',
    _gap: 'Gap',
    _gap_pct: 'Gap %',
    // "Change from Open" family (old-app label; formLabel below).
    _ret_open: 'Chg From Open',
    _ret_open_pct: 'Chg % From Open',
    _high_low_pct: 'High Low %',
    _dollar_vol: 'Dollar Vol',
    _open_ssr: 'SSR at Open',
    _open_high_pct: 'High % From Open',
    _open_low_pct: 'Low % From Open',
    _close_high_pct: 'High %',
    _close_low_pct: 'Low %',
    _range_close_pct: 'Pos Rng %',
  }
  return map[suffix] ?? titleCase(suffix.slice(1))
}

const SUFFIX_FORM_LABELS: Record<string, string> = {
  _vol: 'Volume',
  _chg: 'Change $',
  _chg_pct: 'Change %',
  _ret_open: 'Change From Open',
  _ret_open_pct: 'Change % From Open',
}

function suffixFormLabel(suffix: string, dayPrefix: string): string | undefined {
  const long = SUFFIX_FORM_LABELS[suffix]
  if (!long) return undefined
  // Empty prefix (day0 / D[1], the implicit default) → no leading space.
  return dayPrefix ? `${dayPrefix} ${long}` : long
}

// ─── Group Path Constants ───

const PP = 'Price & Performance'
const IND = 'Indicators'
const CAT = 'Market Data'

// ─── Suffix → Group Mapping ───

/** Map a common OHLCV suffix to its group path under Market Data. */
function suffixToGroup(suffix: string, isDay0: boolean): string {
  switch (suffix) {
    case '_open':
      return isDay0 ? `${PP} > Open` : `${PP} > Open > Open`
    case '_high':
      return isDay0 ? `${PP} > High` : `${PP} > High > High`
    case '_low':
      return isDay0 ? `${PP} > Low` : `${PP} > Low > Low`
    case '_close':
      return isDay0 ? `${PP} > Close` : `${PP} > Close > Close`
    case '_vol':
      return isDay0 ? `${PP} > Volume` : `${PP} > Volume > Volume`
    case '_vw':
      return isDay0 ? `${IND} > VWAP` : `${IND} > VWAP > VWAP`
    case '_range':
      return isDay0 ? `${PP} > Range` : `${PP} > Range > Range`
    case '_chg':
    case '_chg_pct':
      return isDay0 ? `${PP} > Change` : `${PP} > Change > Change (%, $)`
    case '_gap':
    case '_gap_pct':
      return isDay0 ? `${PP} > Gap` : `${PP} > Gap > Gap`
    case '_ret_open':
    case '_ret_open_pct':
      return isDay0 ? `${PP} > Change` : `${PP} > Change > Change From Open (%, $)`
    case '_high_low_pct':
      return isDay0 ? `${PP} > Range` : `${PP} > Range > Range`
    default:
      return PP
  }
}

// ─── 1. Day Offset OHLCV Generator ───

const COMMON_DAY_SUFFIXES = [
  '_open',
  '_high',
  '_low',
  '_close',
  '_vol',
  '_vw',
  '_range',
  '_chg',
  '_chg_pct',
  '_gap',
  '_gap_pct',
  '_ret_open',
  '_ret_open_pct',
  '_high_low_pct',
] as const

function generateDayOHLCV(prefix: string): HistoryColumnDef[] {
  const isD0 = prefix === 'day0'
  // D[1] (today, the reference day) is the implicit default and is NEVER shown;
  // only other day indexes (D[2], D[-1], ...) carry the prefix. Matches the old
  // app and the multi-day family's convention below.
  const p = isD0 ? '' : `${dayLabel(prefix)} `
  const formPrefix = isD0 ? '' : dayLabel(prefix)
  const result: HistoryColumnDef[] = []

  // Date column for every day. day0_date is the record's own date; the
  // relative dates (day1_date, day_m1_date, ...) are the calendar dates behind
  // the multi-day columns. Legacy exposed them too (43 users carried them in
  // column profiles in the 2026-09-07 dump); decided 2026-09-12 to keep them.
  // Chris 2026-09-19: the relative dates are columns you can SHOW, never
  // filter criteria, so they are `displayOnly` (filter pickers drop those).
  // day0_date stays filterable: it is the header's sticky date-scope leaf.
  const dateCol = col(`${prefix}_date`, `${p}Date`, 'date', CAT)
  result.push(isD0 ? dateCol : { ...dateCol, displayOnly: true })

  // Common OHLCV + derived columns
  for (const suffix of COMMON_DAY_SUFFIXES) {
    const key = `${prefix}${suffix}`
    result.push(
      col(
        key,
        `${p}${suffixLabel(suffix)}`,
        inferDataType(key),
        CAT,
        suffixToGroup(suffix, isD0),
        suffixFormLabel(suffix, formPrefix),
      ),
    )
  }

  return result
}

const DAY_PREFIXES = [
  'day0',
  'day1',
  'day2',
  'day3',
  'day4',
  'day5',
  'day_m1',
  'day_m2',
  'day_m3',
  'day_m4',
  'day_m5',
]

function generateAllDayOHLCV(): HistoryColumnDef[] {
  return DAY_PREFIXES.flatMap(generateDayOHLCV)
}

// ─── Day 0 Unique Columns ───

function generateDay0Unique(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  // ── Multi-Day Changes ──
  const MULTI_DAY: Record<number, { short: string; long: string }> = {
    2: { short: '2D', long: '2 Day' },
    3: { short: '3D', long: '3 Day' },
    4: { short: '4D', long: '4 Day' },
    5: { short: '5D', long: '5 Day' },
    10: { short: '10D', long: '10 Day' },
    21: { short: '1M', long: '1 Month' },
    63: { short: '3M', long: '3 Month' },
    126: { short: '6M', long: '6 Month' },
    252: { short: '1Y', long: '1 Year' },
  }
  // No D[1] prefix on the multi-day family: the period alias (2D/1M/1Y) already
  // scopes it, and D[1] (today) is the implicit default — matching the old app,
  // which prefixed only non-today days.
  for (const i of [2, 3, 4, 5, 10, 21, 63, 126, 252]) {
    const { short, long } = MULTI_DAY[i]
    result.push(
      col(
        `day0_chg${i}`,
        `${short} Chg`,
        'currency',
        CAT,
        `${PP} > Change > Multi-Day Change`,
        `${long} Change $`,
      ),
    )
    result.push(
      col(
        `day0_chg${i}_pct`,
        `${short} Chg %`,
        'percent',
        CAT,
        `${PP} > Change > Multi-Day Change`,
        `${long} Change %`,
      ),
    )
  }

  // ── Change Since IPO — parallels the YTD change block below. ──
  result.push(
    col(
      'day0_chgipo',
      'Chg $ Since IPO',
      'currency',
      CAT,
      `${PP} > Change > IPO`,
      'Change $ Since IPO',
    ),
  )
  result.push(
    col(
      'day0_chgipo_pct',
      'Chg % Since IPO',
      'percent',
      CAT,
      `${PP} > Change > IPO`,
      'Change % Since IPO',
    ),
  )
  result.push(
    col('day0_chgipo_high_pct', 'High % Since IPO', 'percent', CAT, `${PP} > Change > IPO`),
  )
  result.push(col('day0_chgipo_low_pct', 'Low % Since IPO', 'percent', CAT, `${PP} > Change > IPO`))

  // ── Close Relative → High/Low/Position in Range groups ──
  result.push(col('day0_open_high_pct', 'High % From Open', 'percent', CAT, `${PP} > High`))
  result.push(col('day0_open_low_pct', 'Low % From Open', 'percent', CAT, `${PP} > Low`))
  result.push(col('day0_close_high_pct', 'High %', 'percent', CAT, `${PP} > High`))
  result.push(col('day0_close_low_pct', 'Low %', 'percent', CAT, `${PP} > Low`))
  result.push(
    col(
      'day0_range_close_pct',
      'Pos Rng %',
      'percent',
      CAT,
      'Position in Range',
      'Position in Range %',
    ),
  )
  result.push(col('day0_open_ssr', 'SSR at Open', 'enum', CAT, `${IND} > SSR`))
  result.push(col('day0_close_ssr', 'SSR at Close', 'enum', CAT, `${IND} > SSR`))

  // ── Dollar Volume ──
  result.push(
    col('day0_dollar_vol', 'Dollar Vol', 'volume', CAT, `${PP} > Volume`, 'Dollar Volume'),
  )

  // ── Trades ──
  result.push(col('day0_trades', 'Trades', 'number', CAT, `${PP} > Trades`))

  // ── Consecutive Days ──
  result.push(col('day0_consec_green', 'Consec Green', 'number', CAT, `${IND} > Consec. Days`))
  result.push(col('day0_consec_red', 'Consec Red', 'number', CAT, `${IND} > Consec. Days`))
  result.push(col('day0_consec_gap_up', 'Consec Gap Up', 'number', CAT, `${IND} > Consec. Days`))
  result.push(
    col('day0_consec_gap_down', 'Consec Gap Down', 'number', CAT, `${IND} > Consec. Days`),
  )
  result.push(
    col('day0_consec_green_bar', 'Consec Green Bars', 'number', CAT, `${IND} > Consec. Days`),
  )
  result.push(col('day0_consec_red_bar', 'Consec Red Bars', 'number', CAT, `${IND} > Consec. Days`))

  // ── Pre-Market → distributed across metric-type groups ──
  result.push(col('day0_pm_open', 'PM Open', 'currency', CAT, `${PP} > Open`))
  result.push(col('day0_pm_range', 'PM Range', 'currency', CAT, `${PP} > Range`))
  result.push(col('day0_pm_vol', 'PM Vol', 'volume', CAT, `${PP} > Volume`))
  result.push(col('day0_pm_high', 'PM High', 'currency', CAT, `${PP} > High`))
  result.push(col('day0_pm_high_time', 'PM High Time', 'string', CAT, `${PP} > Time`))
  result.push(col('day0_pm_high_pct', 'PM High %', 'percent', CAT, `${PP} > High`))
  result.push(col('day0_pm_open_high_pct', 'PM Open High %', 'percent', CAT, `${PP} > High`))
  result.push(
    col(
      'day0_pm_vol_to_hod',
      'PM Vol to HOD',
      'volume',
      CAT,
      `${PP} > Volume > Volume Before / After`,
    ),
  )
  result.push(col('day0_pm_low', 'PM Low', 'currency', CAT, `${PP} > Low`))
  result.push(col('day0_pm_low_time', 'PM Low Time', 'string', CAT, `${PP} > Time`))
  result.push(col('day0_pm_low_pct', 'PM Low %', 'percent', CAT, `${PP} > Low`))
  result.push(col('day0_pm_open_low_pct', 'PM Open Low %', 'percent', CAT, `${PP} > Low`))
  result.push(
    col(
      'day0_pm_vol_to_lod',
      'PM Vol to LOD',
      'volume',
      CAT,
      `${PP} > Volume > Volume Before / After`,
    ),
  )

  // PM Before/After High/Low (price values)
  result.push(
    col('day0_pm_low_after_high', 'PM Low After High', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_pm_low_after_high_time', 'PM Low After High Time', 'string', CAT, `${PP} > Time`),
  )
  result.push(
    col('day0_pm_low_before_high', 'PM Low Before High', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_pm_low_before_high_time', 'PM Low Before High Time', 'string', CAT, `${PP} > Time`),
  )
  result.push(
    col('day0_pm_high_after_low', 'PM High After Low', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_pm_high_after_low_time', 'PM High After Low Time', 'string', CAT, `${PP} > Time`),
  )
  result.push(
    col('day0_pm_high_before_low', 'PM High Before Low', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_pm_high_before_low_time', 'PM High Before Low Time', 'string', CAT, `${PP} > Time`),
  )

  // PM Volume Windows (around high/low)
  for (const pivot of ['high', 'low'] as const) {
    for (const dir of ['m', 'p'] as const) {
      for (const mins of ['15', '10', '05'] as const) {
        const key = `day0_pm_${pivot}_${dir}${mins}_vol`
        const label = `PM ${titleCase(pivot)} ${dir === 'm' ? '-' : '+'}${mins} Vol`
        result.push(col(key, label, 'volume', CAT, `${PP} > Volume > Volume (X)min Before/After`))
      }
    }
  }

  // PM other
  result.push(col('day0_pm_start_time', 'PM Start Time', 'string', CAT, `${PP} > Time`))
  result.push(col('day0_pm_open_range_pct', 'PM Open Range %', 'percent', CAT, 'Position in Range'))
  result.push(
    col('day0_pm_pullback_open_j_pct', 'PM Pullback Open J %', 'percent', CAT, 'Position in Range'),
  )

  // ── High/Low timing → Time group ──
  result.push(col('day0_high_time', 'High Time', 'string', CAT, `${PP} > Time`))
  result.push(
    col('day0_vol_to_hod', 'Vol to HOD', 'volume', CAT, `${PP} > Volume > Volume Before / After`),
  )
  result.push(col('day0_low_time', 'Low Time', 'string', CAT, `${PP} > Time`))
  result.push(
    col('day0_vol_to_lod', 'Vol to LOD', 'volume', CAT, `${PP} > Volume > Volume Before / After`),
  )

  // ── Before/After High/Low extremes (prices → Before/After group, times → Time group) ──
  result.push(
    col('day0_low_after_high', 'Low After High', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(col('day0_low_after_high_time', 'Low After High Time', 'string', CAT, `${PP} > Time`))
  result.push(
    col('day0_low_before_high', 'Low Before High', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_low_before_high_time', 'Low Before High Time', 'string', CAT, `${PP} > Time`),
  )
  result.push(
    col('day0_high_after_low', 'High After Low', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(col('day0_high_after_low_time', 'High After Low Time', 'string', CAT, `${PP} > Time`))
  result.push(
    col('day0_high_before_low', 'High Before Low', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_high_before_low_time', 'High Before Low Time', 'string', CAT, `${PP} > Time`),
  )

  // HOD/LOD Volume Windows
  for (const pivot of ['high', 'low'] as const) {
    for (const dir of ['m', 'p'] as const) {
      for (const mins of ['15', '10', '05'] as const) {
        const key = `day0_${pivot}_${dir}${mins}_vol`
        const label = `${titleCase(pivot)} ${dir === 'm' ? '-' : '+'}${mins} Vol`
        result.push(col(key, label, 'volume', CAT, `${PP} > Volume > Volume (X)min Before/After`))
      }
    }
  }

  // ── After-Hours → distributed across metric-type groups ──
  result.push(col('day0_ah_vol', 'AH Vol', 'volume', CAT, `${PP} > Volume`))
  result.push(col('day0_ah_range', 'AH Range', 'currency', CAT, `${PP} > Range`))
  result.push(col('day0_ah_high', 'AH High', 'currency', CAT, `${PP} > High`))
  result.push(col('day0_ah_high_time', 'AH High Time', 'string', CAT, `${PP} > Time`))
  result.push(
    col(
      'day0_ah_vol_to_hod',
      'AH Vol to HOD',
      'volume',
      CAT,
      `${PP} > Volume > Volume Before / After`,
    ),
  )
  result.push(col('day0_ah_low', 'AH Low', 'currency', CAT, `${PP} > Low`))
  result.push(col('day0_ah_low_time', 'AH Low Time', 'string', CAT, `${PP} > Time`))
  result.push(
    col(
      'day0_ah_vol_to_lod',
      'AH Vol to LOD',
      'volume',
      CAT,
      `${PP} > Volume > Volume Before / After`,
    ),
  )
  result.push(col('day0_ah_close', 'AH Close', 'currency', CAT, `${PP} > Close`))
  result.push(col('day0_ah_chg', 'AH Chg', 'currency', CAT, `${PP} > Change`))
  result.push(col('day0_ah_chg_pct', 'AH Chg %', 'percent', CAT, `${PP} > Change`))
  result.push(col('day0_ah_high_pct', 'AH High %', 'percent', CAT, `${PP} > High`))
  result.push(col('day0_ah_low_pct', 'AH Low %', 'percent', CAT, `${PP} > Low`))
  result.push(col('day0_ah_range_pct', 'AH Range %', 'percent', CAT, 'Position in Range'))

  // AH Before/After extremes
  result.push(
    col('day0_ah_low_after_high', 'AH Low After High', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_ah_low_after_high_time', 'AH Low After High Time', 'string', CAT, `${PP} > Time`),
  )
  result.push(
    col('day0_ah_low_before_high', 'AH Low Before High', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_ah_low_before_high_time', 'AH Low Before High Time', 'string', CAT, `${PP} > Time`),
  )
  result.push(
    col('day0_ah_high_after_low', 'AH High After Low', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_ah_high_after_low_time', 'AH High After Low Time', 'string', CAT, `${PP} > Time`),
  )
  result.push(
    col('day0_ah_high_before_low', 'AH High Before Low', 'currency', CAT, 'Before/After, High/Low'),
  )
  result.push(
    col('day0_ah_high_before_low_time', 'AH High Before Low Time', 'string', CAT, `${PP} > Time`),
  )

  // AH Volume Windows
  for (const pivot of ['high', 'low'] as const) {
    for (const dir of ['m', 'p'] as const) {
      for (const mins of ['15', '10', '05'] as const) {
        const key = `day0_ah_${pivot}_${dir}${mins}_vol`
        const label = `AH ${titleCase(pivot)} ${dir === 'm' ? '-' : '+'}${mins} Vol`
        result.push(col(key, label, 'volume', CAT, `${PP} > Volume > Volume (X)min Before/After`))
      }
    }
  }

  return result
}

// ─── Day 1 Extra Columns (D[2]) ───

function generateDay1Extra(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  // Dollar vol and SSR
  result.push(
    col(
      'day1_dollar_vol',
      'D[2] Dollar Vol',
      'volume',
      CAT,
      `${PP} > Volume > Dollar Volume`,
      'D[2] Dollar Volume',
    ),
  )
  result.push(col('day1_open_ssr', 'D[2] SSR at Open', 'enum', CAT, `${IND} > SSR > SSR`))

  // PM columns → High/Low/Volume/Time (historical sub-groups)
  result.push(col('day1_pm_high', 'D[2] PM High', 'currency', CAT, `${PP} > High > High`))
  result.push(col('day1_pm_high_time', 'D[2] PM High Time', 'string', CAT, `${PP} > Time > Time`))
  result.push(col('day1_pm_high_pct', 'D[2] PM High %', 'percent', CAT, `${PP} > High > High`))
  result.push(col('day1_pm_low', 'D[2] PM Low', 'currency', CAT, `${PP} > Low > Low`))
  result.push(col('day1_pm_low_time', 'D[2] PM Low Time', 'string', CAT, `${PP} > Time > Time`))
  result.push(col('day1_pm_low_pct', 'D[2] PM Low %', 'percent', CAT, `${PP} > Low > Low`))
  result.push(col('day1_pm_vol', 'D[2] PM Vol', 'volume', CAT, `${PP} > Volume > Volume`))

  // Timing (historical)
  result.push(col('day1_high_time', 'D[2] High Time', 'string', CAT, `${PP} > Time > Time`))
  result.push(col('day1_low_time', 'D[2] Low Time', 'string', CAT, `${PP} > Time > Time`))
  result.push(
    col(
      'day1_vol_to_hod',
      'D[2] Vol to HOD',
      'volume',
      CAT,
      `${PP} > Volume > Volume Before / After > Volume Before / After`,
    ),
  )
  result.push(
    col(
      'day1_vol_to_lod',
      'D[2] Vol to LOD',
      'volume',
      CAT,
      `${PP} > Volume > Volume Before / After > Volume Before / After`,
    ),
  )

  // Close relative (historical)
  result.push(
    col('day1_open_high_pct', 'D[2] High % From Open', 'percent', CAT, `${PP} > High > High`),
  )
  result.push(col('day1_open_low_pct', 'D[2] Low % From Open', 'percent', CAT, `${PP} > Low > Low`))
  result.push(col('day1_close_high_pct', 'D[2] High %', 'percent', CAT, `${PP} > High > High`))
  result.push(col('day1_close_low_pct', 'D[2] Low %', 'percent', CAT, `${PP} > Low > Low`))
  result.push(
    col(
      'day1_range_close_pct',
      'D[2] Pos Rng %',
      'percent',
      CAT,
      'Position in Range > Position in Range',
      'D[2] Position in Range %',
    ),
  )

  return result
}

// ─── Other Day Extra Columns ───

function generateOtherDayExtra(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  // day_m1 extras
  result.push(
    col(
      'day_m1_dollar_vol',
      'D[-1] Dollar Vol',
      'volume',
      CAT,
      `${PP} > Volume > Dollar Volume`,
      'D[-1] Dollar Volume',
    ),
  )
  result.push(col('day_m1_open_ssr', 'D[-1] SSR at Open', 'enum', CAT, `${IND} > SSR > SSR`))
  result.push(col('day_m1_close_ssr', 'D[-1] SSR at Close', 'enum', CAT, `${IND} > SSR > SSR`))
  result.push(col('day_m1_trades', 'D[-1] Trades', 'number', CAT, `${PP} > Trades`))

  // day_m2 extras
  result.push(col('day_m2_open_ssr', 'D[-2] SSR at Open', 'enum', CAT, `${IND} > SSR > SSR`))

  // day2 SSR
  result.push(col('day2_open_ssr', 'D[3] SSR at Open', 'enum', CAT, `${IND} > SSR > SSR`))

  // day_m1 after-hours (historical sub-groups)
  const ahKeys: [string, string, DataType, string][] = [
    ['day_m1_ah_high', 'D[-1] AH High', 'currency', `${PP} > High > High`],
    ['day_m1_ah_high_time', 'D[-1] AH High Time', 'string', `${PP} > Time > Time`],
    ['day_m1_ah_high_pct', 'D[-1] AH High %', 'percent', `${PP} > High > High`],
    ['day_m1_ah_low', 'D[-1] AH Low', 'currency', `${PP} > Low > Low`],
    ['day_m1_ah_low_time', 'D[-1] AH Low Time', 'string', `${PP} > Time > Time`],
    ['day_m1_ah_low_pct', 'D[-1] AH Low %', 'percent', `${PP} > Low > Low`],
    ['day_m1_ah_close', 'D[-1] AH Close', 'currency', `${PP} > Close > Close`],
    ['day_m1_ah_vol', 'D[-1] AH Vol', 'volume', `${PP} > Volume > Volume`],
    ['day_m1_ah_range', 'D[-1] AH Range', 'currency', `${PP} > Range > Range`],
    ['day_m1_ah_range_pct', 'D[-1] AH Range %', 'percent', 'Position in Range'],
    ['day_m1_ah_chg', 'D[-1] AH Chg', 'currency', `${PP} > Change > Change (%, $)`],
    ['day_m1_ah_chg_pct', 'D[-1] AH Chg %', 'percent', `${PP} > Change > Change (%, $)`],
  ]
  for (const [k, l, t, g] of ahKeys) result.push(col(k, l, t, CAT, g))

  // day_m1 pre-market — parallels day1_pm_* (High/Low/Volume/Range groups)
  const pmKeys: [string, string, DataType, string][] = [
    ['day_m1_pm_high', 'D[-1] PM High', 'currency', `${PP} > High > High`],
    ['day_m1_pm_high_pct', 'D[-1] PM High %', 'percent', `${PP} > High > High`],
    ['day_m1_pm_low', 'D[-1] PM Low', 'currency', `${PP} > Low > Low`],
    ['day_m1_pm_low_pct', 'D[-1] PM Low %', 'percent', `${PP} > Low > Low`],
    ['day_m1_pm_vol', 'D[-1] PM Vol', 'volume', `${PP} > Volume > Volume`],
    ['day_m1_pm_range', 'D[-1] PM Range', 'currency', `${PP} > Range > Range`],
    ['day_m1_pm_open_range_pct', 'D[-1] PM Open Range %', 'percent', 'Position in Range'],
  ]
  for (const [k, l, t, g] of pmKeys) result.push(col(k, l, t, CAT, g))

  // day_m1 close/open relative to High/Low + position-in-range (historical
  // equivalents of the day0_open_high_pct / day0_range_close_pct family)
  result.push(
    col('day_m1_open_high_pct', 'D[-1] High % From Open', 'percent', CAT, `${PP} > High > High`),
  )
  result.push(col('day_m1_close_high_pct', 'D[-1] High %', 'percent', CAT, `${PP} > High > High`))
  result.push(
    col('day_m1_open_low_pct', 'D[-1] Low % From Open', 'percent', CAT, `${PP} > Low > Low`),
  )
  result.push(col('day_m1_close_low_pct', 'D[-1] Low %', 'percent', CAT, `${PP} > Low > Low`))
  result.push(
    col(
      'day_m1_range_close_pct',
      'D[-1] Pos Rng %',
      'percent',
      CAT,
      'Position in Range',
      'D[-1] Position in Range %',
    ),
  )

  // day_m1 multi-day change % (D[-1]'s trailing 2-5 day change)
  for (const i of [2, 3, 4, 5]) {
    result.push(
      col(
        `day_m1_chg${i}_pct`,
        `D[-1] ${i}D Chg %`,
        'percent',
        CAT,
        `${PP} > Change > Multi-Day Change > Multi-Day Change`,
      ),
    )
  }

  // day_m1 consecutive-day counters (Consec. Days group)
  const consecKeys: [string, string][] = [
    ['day_m1_consec_green', 'D[-1] Consec Green'],
    ['day_m1_consec_red', 'D[-1] Consec Red'],
    ['day_m1_consec_green_bar', 'D[-1] Consec Green Bars'],
    ['day_m1_consec_red_bar', 'D[-1] Consec Red Bars'],
    ['day_m1_consec_gap_up', 'D[-1] Consec Gap Up'],
    ['day_m1_consec_gap_down', 'D[-1] Consec Gap Down'],
  ]
  for (const [k, l] of consecKeys) result.push(col(k, l, 'number', CAT, `${IND} > Consec. Days`))

  // day_m1 ADTV (mirrors day0_adtv_*)
  for (const n of [7, 14, 30, 60]) {
    result.push(col(`day_m1_adtv_${n}`, `D[-1] ADTV ${n}`, 'volume', CAT, `${IND} > ADTV`))
  }

  // day_m1/m2/m3 ATR $ + % (day_m1_atr_7/7_pct already live in generateExtendedMetrics)
  result.push(col('day_m1_atr_7', 'D[-1] ATR 7', 'currency', CAT, `${IND} > ATR`))
  result.push(col('day_m1_atr_14', 'D[-1] ATR 14', 'currency', CAT, `${IND} > ATR`))
  result.push(col('day_m1_atr_14_pct', 'D[-1] ATR 14 %', 'percent', CAT, `${IND} > ATR`))
  result.push(col('day_m2_atr_14', 'D[-2] ATR 14', 'currency', CAT, `${IND} > ATR`))
  result.push(col('day_m2_atr_14_pct', 'D[-2] ATR 14 %', 'percent', CAT, `${IND} > ATR`))
  result.push(col('day_m3_atr_14', 'D[-3] ATR 14', 'currency', CAT, `${IND} > ATR`))
  result.push(col('day_m3_atr_14_pct', 'D[-3] ATR 14 %', 'percent', CAT, `${IND} > ATR`))

  // day_m2..m5 dollar volume (mirrors day_m1_dollar_vol / day1_dollar_vol)
  for (const day of ['day_m2', 'day_m3', 'day_m4', 'day_m5']) {
    result.push(
      col(
        `${day}_dollar_vol`,
        `${dayLabel(day)} Dollar Vol`,
        'volume',
        CAT,
        `${PP} > Volume > Dollar Volume`,
        `${dayLabel(day)} Dollar Volume`,
      ),
    )
  }

  // day_m1/m2 pivot point (nested under Pivot Points, mirrors day0_pp_pivot)
  result.push(
    col('day_m1_pp_pivot', 'D[-1] Pivot', 'currency', CAT, `${IND} > Pivot Points > Pivot Point`),
  )
  result.push(
    col('day_m2_pp_pivot', 'D[-2] Pivot', 'currency', CAT, `${IND} > Pivot Points > Pivot Point`),
  )

  // day_m1 multi-day high/low % (as observed on D[-1]) — pct-only, mirrors the
  // day_N_high_pct/low_pct family nested one level deeper (old app:
  // High > Multi-Day Highs > Multi-Day Highs).
  for (const n of [2, 3, 4, 5, 10, 21, 63, 126, 252]) {
    result.push(
      col(
        `day_m1_${n}_high_pct`,
        `D[-1] ${n}D High %`,
        'percent',
        CAT,
        `${PP} > High > Multi-Day Highs > Multi-Day Highs`,
      ),
    )
    result.push(
      col(
        `day_m1_${n}_low_pct`,
        `D[-1] ${n}D Low %`,
        'percent',
        CAT,
        `${PP} > Low > Multi-Day Lows > Multi-Day Lows`,
      ),
    )
  }

  return result
}

// ─── 2. Hourly Time Bucket Generator ───

const HALF_HOUR_WINDOWS = [
  '0400_0430',
  '0430_0500',
  '0500_0530',
  '0530_0600',
  '0600_0630',
  '0630_0700',
  '0700_0730',
  '0730_0800',
  '0800_0830',
  '0830_0900',
  '0900_0930',
  '0930_1000',
  '1000_1030',
  '1030_1100',
  '1100_1130',
  '1130_1200',
  '1200_1230',
  '1230_1300',
  '1300_1330',
  '1330_1400',
  '1400_1430',
  '1430_1500',
  '1500_1530',
  '1530_1600',
  '1600_1630',
  '1630_1700',
  '1700_1730',
  '1730_1800',
  '1800_1830',
  '1830_1900',
  '1900_1930',
  '1930_2000',
] as const

const FULL_HOUR_WINDOWS = [
  '0400_0500',
  '0500_0600',
  '0600_0700',
  '0700_0800',
  '0800_0900',
  '0900_1000',
  '1000_1100',
  '1100_1200',
  '1200_1300',
  '1300_1400',
  '1400_1500',
  '1500_1600',
  '1600_1700',
  '1700_1800',
  '1800_1900',
  '1900_2000',
] as const

const HOURLY_SUFFIXES = [
  '_open',
  '_high',
  '_low',
  '_close',
  '_vol',
  '_vw',
  '_chg',
  '_chg_pct',
] as const

function formatTimeWindow(window: string): string {
  const [start, end] = [window.slice(0, 4), window.slice(5)]
  return `${start.slice(0, 2)}:${start.slice(2)}-${end.slice(0, 2)}:${end.slice(2)}`
}

function generateHourlyBuckets(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  // 30-minute windows
  for (const window of HALF_HOUR_WINDOWS) {
    const timeLabel = formatTimeWindow(window)
    for (const suffix of HOURLY_SUFFIXES) {
      const key = `day0_h_${window}${suffix}`
      const label = `${timeLabel} ${suffixLabel(suffix)}`
      result.push(
        col(key, label, inferDataType(key), CAT, `Brackets (30/60min) > D[1] 30min > ${timeLabel}`),
      )
    }
  }

  // 60-minute windows
  for (const window of FULL_HOUR_WINDOWS) {
    const timeLabel = formatTimeWindow(window)
    for (const suffix of HOURLY_SUFFIXES) {
      const key = `day0_h_${window}${suffix}`
      const label = `${timeLabel} ${suffixLabel(suffix)}`
      result.push(
        col(key, label, inferDataType(key), CAT, `Brackets (30/60min) > D[1] 60min > ${timeLabel}`),
      )
    }
  }

  return result
}

// ─── 3. Spike/Drop Generator ───

const SPIKE_DROP_INTERVALS = [5, 10, 15, 30, 60] as const

function generateSpikeDropColumns(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  for (const n of SPIKE_DROP_INTERVALS) {
    // Spike columns — nested by interval
    result.push(
      col(
        `day0_spike_${n}min_max`,
        `Spike ${n}m Max`,
        'currency',
        CAT,
        `Spike & Drop > Spike > ${n}min`,
      ),
    )
    result.push(
      col(
        `day0_spike_${n}min_max_pct`,
        `Spike ${n}m Max %`,
        'percent',
        CAT,
        `Spike & Drop > Spike > ${n}min`,
      ),
    )
    result.push(
      col(
        `day0_spike_${n}min_max_time`,
        `Spike ${n}m Max Time`,
        'string',
        CAT,
        `Spike & Drop > Spike > ${n}min`,
      ),
    )
    result.push(
      col(
        `day0_spike_${n}min_max_vol`,
        `Spike ${n}m Max Vol`,
        'volume',
        CAT,
        `Spike & Drop > Spike > ${n}min`,
      ),
    )
    result.push(
      col(
        `day0_spike_${n}min_max_vw`,
        `Spike ${n}m Max VW`,
        'currency',
        CAT,
        `Spike & Drop > Spike > ${n}min`,
      ),
    )
    // Drop columns — flat under Drop
    result.push(
      col(`day0_drop_${n}min_min`, `Drop ${n}m Min`, 'currency', CAT, 'Spike & Drop > Drop'),
    )
    result.push(
      col(`day0_drop_${n}min_min_pct`, `Drop ${n}m Min %`, 'percent', CAT, 'Spike & Drop > Drop'),
    )
    result.push(
      col(
        `day0_drop_${n}min_min_time`,
        `Drop ${n}m Min Time`,
        'string',
        CAT,
        'Spike & Drop > Drop',
      ),
    )
    result.push(
      col(`day0_drop_${n}min_min_vol`, `Drop ${n}m Min Vol`, 'volume', CAT, 'Spike & Drop > Drop'),
    )
    result.push(
      col(`day0_drop_${n}min_min_vw`, `Drop ${n}m Min VW`, 'currency', CAT, 'Spike & Drop > Drop'),
    )
  }

  return result
}

// ─── 4. Multi-Minute Candle Generator ───

// Day 0: full candles (1-15 individual, then 30-min steps up to 360)
const DAY0_MULTI_MINUTE_INTERVALS = [
  1, 2, 3, 4, 5, 10, 15, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360,
]
const DAY0_MULTI_MIN_SUFFIXES = [
  '_high',
  '_high_pct',
  '_low',
  '_low_pct',
  '_close',
  '_close_pct',
  '_vol',
] as const

// Day 1: only percent variants
const DAY1_MULTI_MINUTE_INTERVALS = [
  1, 2, 3, 4, 5, 15, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360,
]
const DAY1_MULTI_MIN_SUFFIXES = ['_high_pct', '_low_pct', '_close_pct'] as const

function generateMultiMinuteCandles(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  // Day 0 candles
  for (const n of DAY0_MULTI_MINUTE_INTERVALS) {
    for (const suffix of DAY0_MULTI_MIN_SUFFIXES) {
      const key = `day0_${n}min${suffix}`
      const label = `${n}m ${suffixLabel(suffix)}`
      result.push(col(key, label, inferDataType(key), CAT, `HLCV% after X minutes > ${n}min`))
    }
  }

  // Day 1 candles (pct only)
  for (const n of DAY1_MULTI_MINUTE_INTERVALS) {
    for (const suffix of DAY1_MULTI_MIN_SUFFIXES) {
      const key = `day1_${n}min${suffix}`
      const label = `D[2] ${n}m ${suffixLabel(suffix)}`
      result.push(col(key, label, inferDataType(key), CAT, `HLCV% after X minutes > ${n}min`))
    }
  }

  return result
}

// ─── 5. Daily Indicators Generator ───

function generateDailyIndicators(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  // EMA
  const emaPeriods = [9, 21, 50, 72, 89, 100, 200]
  for (const n of emaPeriods) {
    result.push(col(`day0_ema_${n}`, `EMA ${n}`, 'currency', CAT, `${IND} > Daily EMA > EMA`))
    result.push(
      col(
        `day0_ema_${n}_dist_pct`,
        `EMA ${n} Dist %`,
        'percent',
        CAT,
        `${IND} > Daily EMA > EMA Chg %`,
      ),
    )
  }

  // ADTV
  const adtvPeriods = [7, 14, 30, 60]
  for (const n of adtvPeriods) {
    result.push(col(`day0_adtv_${n}`, `ADTV ${n}`, 'volume', CAT, `${IND} > ADTV`))
  }

  // RVOL
  result.push(col('day0_rvol_5', 'RVOL 5', 'number', CAT, `${PP} > Volume > RVOL`))

  // ATR
  const atrPeriods = [7, 14, 30, 60]
  for (const n of atrPeriods) {
    result.push(col(`day0_atr_${n}`, `ATR ${n}`, 'currency', CAT, `${IND} > ATR`))
  }

  // Multi-Day Highs / Lows / Range (+ % variants, + Position-in-Range %).
  // 2/3/4/10 were entirely missing; 5/21/63/126/252 previously had only the
  // raw $ level, never the _pct siblings.
  const rangePeriods = [2, 3, 4, 5, 10, 21, 63, 126, 252]
  for (const n of rangePeriods) {
    result.push(
      col(`day_${n}_high`, `${n}D High`, 'currency', CAT, `${PP} > High > Multi-Day Highs`),
    )
    result.push(
      col(`day_${n}_high_pct`, `${n}D High %`, 'percent', CAT, `${PP} > High > Multi-Day Highs`),
    )
    result.push(col(`day_${n}_low`, `${n}D Low`, 'currency', CAT, `${PP} > Low > Multi-Day Lows`))
    result.push(
      col(`day_${n}_low_pct`, `${n}D Low %`, 'percent', CAT, `${PP} > Low > Multi-Day Lows`),
    )
    result.push(
      col(`day_${n}_range`, `${n}D Range`, 'currency', CAT, `${PP} > Range > Multi-Day Range`),
    )
    result.push(
      col(
        `day_${n}_range_pct`,
        `${n}D Pos Rng %`,
        'percent',
        CAT,
        'Position in Range > Multi-Day Position in Range',
      ),
    )
  }

  // 1-day position-in-range % (no matching raw day_1_high/low/range — % only)
  result.push(
    col(
      'day_1_range_pct',
      '1D Pos Rng %',
      'percent',
      CAT,
      'Position in Range > Multi-Day Position in Range',
    ),
  )

  // IPO-day and YTD reference High/Low (raw $ levels; day_ytd_range/day_ytd_point
  // and the day0_chgytd_*/day0_chgipo_* % variants live in generateExtendedMetrics).
  result.push(col('day_ipo_high', 'IPO High', 'currency', CAT, `${PP} > High > Multi-Day Highs`))
  result.push(col('day_ipo_low', 'IPO Low', 'currency', CAT, `${PP} > Low > Multi-Day Lows`))
  result.push(col('day_ipo_range', 'IPO Range', 'currency', CAT, `${PP} > Range > Multi-Day Range`))
  result.push(
    col(
      'day_ipo_range_pct',
      'IPO Pos Rng %',
      'percent',
      CAT,
      'Position in Range > Multi-Day Position in Range',
    ),
  )
  result.push(col('day_ytd_high', 'YTD High', 'currency', CAT, `${PP} > High > Multi-Day Highs`))
  result.push(col('day_ytd_low', 'YTD Low', 'currency', CAT, `${PP} > Low > Multi-Day Lows`))
  result.push(
    col(
      'day_ytd_range_pct',
      'YTD Pos Rng %',
      'percent',
      CAT,
      'Position in Range > Multi-Day Position in Range',
    ),
  )

  return result
}

// ─── 6. Extended Metrics Generator ───

// SMA, ATR %, intraday RVOL, Pivot Points, Opendrive, prior-day positioning, and
// YTD. These all exist in the backend view but were previously parked in the
// not-surfaced snapshot rather than built into the catalog.
function generateExtendedMetrics(): HistoryColumnDef[] {
  const result: HistoryColumnDef[] = []

  // ── SMA — mirrors the EMA family (price level + distance %). ──
  const smaPeriods = [9, 21, 50, 72, 89, 100, 200]
  for (const n of smaPeriods) {
    result.push(col(`day0_sma_${n}`, `SMA ${n}`, 'currency', CAT, `${IND} > Daily SMA > SMA`))
    result.push(
      col(
        `day0_sma_${n}_dist_pct`,
        `SMA ${n} Dist %`,
        'percent',
        CAT,
        `${IND} > Daily SMA > SMA Chg %`,
      ),
    )
  }

  // ── ATR % — ATR normalized to price (the plain ATR $ periods live in generateDailyIndicators). ──
  result.push(col('day0_atr_14_pct', 'ATR 14 %', 'percent', CAT, `${IND} > ATR`))
  result.push(col('day_m1_atr_7_pct', 'D[-1] ATR 7 %', 'percent', CAT, `${IND} > ATR`))

  // ── Relative ATR — ATR reached N minutes into the session, relative to the
  //    full-day ATR. ──
  for (const n of [5, 10, 15, 30]) {
    result.push(col(`day0_relativeatr_${n}min`, `Rel. ATR ${n}m`, 'number', CAT, `${IND} > ATR`))
  }

  // ── Intraday RVOL — relative volume vs 5-day avg N minutes into the session,
  //    plus the categorical "points" buckets. (Full-day day0_rvol_5 is a daily indicator.) ──
  for (const n of [5, 10, 15, 30]) {
    result.push(
      col(`day0_rvol_5_${n}min`, `RVOL 5 (${n}m)`, 'number', CAT, `${PP} > Volume > RVOL`),
    )
  }
  result.push(col('rvol_points', 'RVOL Points', 'string', CAT, `${PP} > Volume > RVOL`))
  result.push(col('rvol_points_5', 'RVOL Points 5', 'string', CAT, `${PP} > Volume > RVOL`))

  // ── Pivot Points — classic floor-trader pivot + R1-R3 / S1-S3 levels, plus
  //    the % change from each level. ──
  result.push(col('day0_pp_pivot', 'Pivot', 'currency', CAT, `${IND} > Pivot Points`))
  result.push(col('pp_pivot_pct', 'Pivot %', 'percent', CAT, `${IND} > Pivot Points`))
  for (const n of [1, 2, 3]) {
    result.push(col(`day0_pp_r${n}`, `R${n}`, 'currency', CAT, `${IND} > Pivot Points`))
    result.push(col(`pp_r${n}_pct`, `R${n} %`, 'percent', CAT, `${IND} > Pivot Points`))
  }
  for (const n of [1, 2, 3]) {
    result.push(col(`day0_pp_s${n}`, `S${n}`, 'currency', CAT, `${IND} > Pivot Points`))
    result.push(col(`pp_s${n}_pct`, `S${n} %`, 'percent', CAT, `${IND} > Pivot Points`))
  }

  // ── Opendrive — how far price drives off the open: relative (ropendrive) and
  //    ATR-normalized (opendriveatr14) at N minutes, plus categorical "points" buckets. ──
  for (const n of [5, 10, 15, 30]) {
    result.push(
      col(`day0_ropendrive_${n}min`, `R-Opendrive (${n}m)`, 'number', CAT, `${IND} > Opendrive`),
    )
  }
  for (const n of [5, 10, 15, 30, 60]) {
    result.push(
      col(
        `day0_opendriveatr14_${n}min`,
        `Opendrive/ATR14 (${n}m)`,
        'number',
        CAT,
        `${IND} > Opendrive`,
      ),
    )
  }
  result.push(col('opendrive_points', 'Opendrive Points', 'string', CAT, `${IND} > Opendrive`))
  result.push(col('opendrive_points_5', 'Opendrive Points 5', 'string', CAT, `${IND} > Opendrive`))

  // ── Prior-day positioning — where open/close sat within each prior day's range
  //    (day0's equivalents live in generateDay0Unique). ──
  const positioningDays = ['day_m2', 'day_m3', 'day_m4', 'day_m5']
  const positioning: { suffix: string; group: string }[] = [
    { suffix: '_open_high_pct', group: `${PP} > High` },
    { suffix: '_open_low_pct', group: `${PP} > Low` },
    { suffix: '_close_high_pct', group: `${PP} > High` },
    { suffix: '_close_low_pct', group: `${PP} > Low` },
    { suffix: '_range_close_pct', group: 'Position in Range' },
  ]
  for (const day of positioningDays) {
    for (const { suffix, group } of positioning) {
      result.push(
        col(`${day}${suffix}`, `${dayLabel(day)} ${suffixLabel(suffix)}`, 'percent', CAT, group),
      )
    }
  }

  // ── Year-to-date — change since Jan 1 (with intraday high/low reach) + YTD reference levels. ──
  result.push(
    col('day0_chgytd', 'YTD Chg', 'currency', CAT, `${PP} > Change > YTD`, 'YTD Change $'),
  )
  result.push(
    col('day0_chgytd_pct', 'YTD Chg %', 'percent', CAT, `${PP} > Change > YTD`, 'YTD Change %'),
  )
  result.push(col('day0_chgytd_high_pct', 'YTD High %', 'percent', CAT, `${PP} > Change > YTD`))
  result.push(col('day0_chgytd_low_pct', 'YTD Low %', 'percent', CAT, `${PP} > Change > YTD`))
  result.push(col('day_ytd_point', 'YTD Point', 'currency', CAT, `${PP} > Change > YTD`))
  result.push(col('day_ytd_range', 'YTD Range', 'currency', CAT, `${PP} > Change > YTD`))

  // ── All-Day / All-Session extremes — the pre-market+regular+after-hours
  //    combined high/low, and the same for the regular+extended "session". ──
  result.push(col('all_day_h', 'High - All Day', 'currency', CAT, `${PP} > High`))
  result.push(col('chg_allday_h_pc_pct', 'High % - All Day', 'percent', CAT, `${PP} > High`))
  result.push(col('all_day_l', 'Low - All Day', 'currency', CAT, `${PP} > Low`))
  result.push(col('chg_allday_l_pc_pct', 'Low % - All Day', 'percent', CAT, `${PP} > Low`))
  result.push(
    col('chg_allday_range_pct', 'Pos Rng % - All Day', 'percent', CAT, 'Position in Range'),
  )
  result.push(col('allsession_h', 'High - All Sess', 'currency', CAT, `${PP} > High`))
  result.push(col('chg_allsession_h_pc_pct', 'High % - All Sess', 'percent', CAT, `${PP} > High`))
  result.push(col('allsession_l', 'Low - All Sess', 'currency', CAT, `${PP} > Low`))
  result.push(col('chg_allsession_l_pc_pct', 'Low % - All Sess', 'percent', CAT, `${PP} > Low`))
  result.push(
    col('chg_allsession_range_pct', 'Pos Rng % - All Sess', 'percent', CAT, 'Position in Range'),
  )

  // ── Extended-hours volume + position-in-range (pre-market + after-hours combined). ──
  result.push(col('ext_hours_v', 'Ext Hours Vol', 'volume', CAT, `${PP} > Volume`))
  result.push(
    col('ext_hours_pos_rng_pct', 'Pos Rng % - Ext Hours', 'percent', CAT, 'Position in Range'),
  )

  return result
}

// ─── Company ───

const COMPANY_COLUMNS: HistoryColumnDef[] = [
  col('ticker', 'Ticker', 'string', 'Company'),
  col('raw_ticker', 'Raw Ticker', 'string', 'Company'),
  col('exchange', 'Exchange', 'enum', 'Company'),
  col('sector', 'Sector', 'enum', 'Company'),
  col('industry', 'Industry', 'enum', 'Company'),
  col('ipo_date', 'IPO Date', 'string', 'Company'),
  col('country', 'Country', 'enum', 'Company'),
  col('country_iso', 'Country Code', 'enum', 'Company'),
  col('share_group', 'Share Group', 'enum', 'Company'),
]

// ─── Financials ───
// Old app "Financials" category (flat, no sub-groups) — dropped from the new
// catalog entirely; restored here with dataType/columnType parity to the
// scanner catalog's identical keys (`scanner-column-defs.ts`).
const FINANCIALS_COLUMNS: HistoryColumnDef[] = [
  col('cashandcashequivalents', 'Cash & Equiv', 'volume', 'Financials'),
  col('cashcashequivalentsandshortterminvestments', 'Short Term Cash', 'volume', 'Financials'),
  col('currentassets', 'Current Assets', 'volume', 'Financials'),
  col('currentliabilities', 'Current Liabilities', 'volume', 'Financials'),
  col('currentratio', 'Current Ratio', 'number', 'Financials'),
  col('dilutedeps', 'Diluted EPS', 'currency', 'Financials'),
  col('eps', 'EPS', 'currency', 'Financials'),
  col('eps_growth_q', 'EPS QoQ %', 'percent', 'Financials'),
  col('grossmargin', 'Gross Margin', 'volume', 'Financials'),
  col('netincome', 'Net Income', 'volume', 'Financials'),
  col('netmargin', 'Net Margin', 'volume', 'Financials'),
  col('revenue_growth_q', 'Revenue QoQ %', 'percent', 'Financials'),
  col('roa', 'ROA', 'percent', 'Financials'),
  col('roe', 'ROE', 'percent', 'Financials'),
]

// Frontend-only: rendered client-side from /v2/tags/full; never sent to
// the history API as a column. As a FILTER it's an enum whose options are
// injected live by useHistoryColumns (value = tag id string, label = name);
// the datasource extracts tags leaves into the request's tag_filter field.
// Own top-level category so it's immediately findable in the picker.
const TAGS_COLUMNS: HistoryColumnDef[] = [col('tags', 'Tags', 'enum', 'Tags')]

// Frontend-only shape-similarity filter. Selecting it in the picker opens the exemplar builder
// (SimilarPatternDialog); the leaf stores the chosen PatternCondition as a JSON string under a
// plain string/eq shape (so it passes the form's validity + profileToQuery paths untouched) and a
// custom row renders it. Every history filter consumer splits it out into the request's
// `pattern_filter` field (splitPatternFilter). The matching RESULT column reuses this same key and
// renders as "Match" in the grid (see buildHistoryColDefs). Own category so it's findable.
const PATTERN_COLUMNS: HistoryColumnDef[] = [
  col('pattern_match', 'Similar pattern', 'string', 'Similar pattern'),
]

/**
 * Column keys that exist in the frontend catalog but must NOT be forwarded to
 * the history API (the backend has no concept of them). Strip these from the
 * `columns` array before building the API request.
 */
export const FRONTEND_ONLY_HISTORY_COLUMNS: Set<string> = new Set(['tags', 'pattern_match'])

// ─── Share Structure ───

const SHARE_STRUCTURE_COLUMNS: HistoryColumnDef[] = [
  col('sharesfloat', 'Float', 'volume', 'Share Structure'),
  col('sharesoutstanding', 'Shares Outstanding', 'volume', 'Share Structure'),
  col('marketcap', 'Market Cap', 'volume', 'Share Structure'),
  col('percentinsiders', 'Insiders %', 'percent', 'Share Structure'),
  col('percentinstitutions', 'Institutions %', 'percent', 'Share Structure'),
]

/**
 * The prior trading day's snapshot (mv_historical_share_structure joined on
 * day_m1_date, ettalchemy prior_day_models.py): the share structure known before
 * day0's open, which the Backtest report's filters may use where the day0 snapshot
 * is blocked (Chris, 2026-10-30). Built from the day0 entry so the label, columnType
 * and allowNull match it.
 */
function priorDayOf(day0Key: string): HistoryColumnDef {
  const day0 = SHARE_STRUCTURE_COLUMNS.find((c) => c.key === day0Key)
  if (!day0) throw new Error(`No day0 share-structure column ${day0Key}`)
  return { ...day0, key: `day_m1_${day0Key}`, label: `D[-1] ${day0.label}` }
}

const PRIOR_DAY_SHARE_STRUCTURE_COLUMNS: HistoryColumnDef[] = [
  priorDayOf('sharesfloat'),
  priorDayOf('sharesoutstanding'),
  priorDayOf('percentinsiders'),
  priorDayOf('percentinstitutions'),
]

// ─── Halts ───

const HALTS_COLUMNS: HistoryColumnDef[] = [
  col('halts_count', 'Halts Count', 'number', 'Halts'),
  col('first_halt', 'First Halt', 'string', 'Halts'),
  col('last_halt', 'Last Halt', 'string', 'Halts'),
]

// ─── Assemble Full Catalog ───

// Category order matches the legacy app's history GROUPS: Market Data (the
// price/performance/indicator columns — most-used, so first) → Company → Share
// Structure → Halts → Financials. `Tags` is a new frontend-only column, placed
// last. Within `Market Data`, WS3's interleaved `childOrder` render keeps
// direct high-use columns above sub-folders.
// Everything but the Similar pattern column; the two published catalogs below
// differ only in whether it sits between Financials and Tags.
const CATALOG_BEFORE_PATTERNS: HistoryColumnDef[] = [
  // ── Market Data (all generators emit category 'Market Data') ──
  ...generateAllDayOHLCV(),
  ...generateDay0Unique(),
  ...generateDay1Extra(),
  ...generateOtherDayExtra(),
  ...generateDailyIndicators(),
  ...generateSpikeDropColumns(),
  ...generateHourlyBuckets(),
  ...generateMultiMinuteCandles(),
  ...generateExtendedMetrics(),
  // ── Reference categories ──
  ...COMPANY_COLUMNS,
  ...SHARE_STRUCTURE_COLUMNS,
  ...PRIOR_DAY_SHARE_STRUCTURE_COLUMNS,
  ...HALTS_COLUMNS,
  ...FINANCIALS_COLUMNS,
]

/**
 * The history column catalog. Similar pattern is NOT in it: that feature is
 * behind the `similarpatterns-release` flag, so only History offers it, by
 * swapping in HISTORY_COLUMN_CATALOG_WITH_PATTERNS (see useHistoryColumns).
 */
export const HISTORY_COLUMN_CATALOG: HistoryColumnDef[] = [
  ...CATALOG_BEFORE_PATTERNS,
  ...TAGS_COLUMNS,
]

/** The catalog plus the Similar pattern column, for History while its flag is on. */
export const HISTORY_COLUMN_CATALOG_WITH_PATTERNS: HistoryColumnDef[] = [
  ...CATALOG_BEFORE_PATTERNS,
  ...PATTERN_COLUMNS,
  ...TAGS_COLUMNS,
]

// ─── Derived Exports ───

/** Lookup map for O(1) column access. */
// Includes the pattern column whatever the flag, so a saved view that already
// carries it still resolves.
export const HISTORY_COLUMN_MAP: Record<string, HistoryColumnDef> = Object.fromEntries(
  HISTORY_COLUMN_CATALOG_WITH_PATTERNS.map((col) => [col.key, col]),
)

function groupByCategory(catalog: HistoryColumnDef[]): Record<string, HistoryColumnDef[]> {
  return catalog.reduce(
    (acc, col) => {
      ;(acc[col.category] ??= []).push(col)
      return acc
    },
    {} as Record<string, HistoryColumnDef[]>,
  )
}

/** Columns grouped by category. */
export const HISTORY_COLUMNS_BY_CATEGORY: Record<string, HistoryColumnDef[]> =
  groupByCategory(HISTORY_COLUMN_CATALOG)

/** Columns grouped by category, Similar pattern included. */
export const HISTORY_COLUMNS_BY_CATEGORY_WITH_PATTERNS: Record<string, HistoryColumnDef[]> =
  groupByCategory(HISTORY_COLUMN_CATALOG_WITH_PATTERNS)

export function getHistoryColumnDef(key: string): HistoryColumnDef | undefined {
  return HISTORY_COLUMN_MAP[key]
}

/** Default columns shown in the records grid (single-ticker context, e.g. dashboard). */
// The Ticker Stats "General" preset's columns (DEFAULT_TICKER_STATS_COLUMN_PROFILES);
// a test pins the two together.
export const DEFAULT_RECORDS_COLUMNS = [
  'day0_date',
  'day0_open',
  'day0_gap_pct',
  'day0_chg_pct',
  'day0_vol',
  'day0_high_time',
  'day0_low_time',
]

/**
 * Default columns for the multi-ticker History records grid, ticker first.
 * Reshaped 2026-09-11 (Chris) to the legacy "General" set: the intraday texture
 * legacy users had (open, PM volume, from-open percentages, high/low times).
 * DEFAULT_HISTORY_COLUMN_PROFILES' "Default" preset mirrors this list so
 * "Default" is a true reset.
 */
export const DEFAULT_HISTORY_RECORDS_COLUMNS = [
  'ticker',
  'day0_date',
  'day0_open',
  'day0_pm_vol',
  'day0_gap_pct',
  'day0_chg_pct',
  'day0_vol',
  'day0_open_high_pct',
  'day0_open_low_pct',
  'day0_ret_open_pct',
  'day0_high_time',
  'day0_low_time',
]
